import { CircleHelp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Dialog } from "./Dialog";
import type { InformationDialogProp } from "~/types/ui";

export function InformationDialog({
  title,
  description,
  hoverText,
  dialogText,
  url,
  hoverChild,
  children,
}: InformationDialogProp) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const iframeSrc = url?.replace(
    /youtube\.com\/watch\?v=([^&]+)/,
    "youtube.com/embed/$1",
  );

  const dialogContent = iframeSrc ? (
    <iframe
      src={iframeSrc}
      className="w-full h-full border-0"
      style={{ minHeight: "60vh" }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  ) : children != null ? (
    children
  ) : (
    <p className="text-sm text-muted-foreground">
      {dialogText ?? hoverText ?? ""}
    </p>
  );

  const tooltipContent =
    hoverChild != null
      ? hoverChild
      : (hoverText ?? t("information.defaultHover"));

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center text-blue-400 hover:text-blue-500 transition-colors"
            aria-label={title}
          >
            <CircleHelp className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>

      <Dialog
        title={title ?? t("information.defaultTitle")}
        description={description ?? t("information.defaultDescription")}
        width={iframeSrc ? "95vw" : children != null ? "auto" : "25vw"}
        maxWidth="95vw"
        open={open}
        onOpenChange={setOpen}
        externalTrigger
      >
        {dialogContent}
      </Dialog>
    </>
  );
}
